"use client";

import { c as _c } from "react/compiler-runtime";
import AllergiesDialog from "../../dialogs/allergies-dialog";
import DynamicFieldsDialog from "../../dialogs/dynamic-fields-dialog";
import FamilyHistoryDialog from "../../dialogs/family-history-dialog";
import MedicalHistoryDialog from "../../dialogs/medical-history-dialog";
import PatientMedicationsDialog from "../../dialogs/patient-medications-dialog";
import PatientReportedSymptomsDialog from "../../dialogs/patient-reported-symptoms-dialog";
import PrescriptionsDialog from "../../dialogs/prescriptions-dialog";
import { FiEdit } from "react-icons/fi";
import LabeledValue from "@/components/labeled-value";
import { Button } from "@/components/ui";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { formatToHumanReadable } from "@/lib/format-data";
import { getAllergies, getFamilyHistory, getHealthFieldId, getMedicalHistory, getMedications, getPatientReportedSymptoms, getPrescriptions, getUnscreenedValues } from "@/lib/patient-health-fields";
import { cx } from "@/lib/tailwind-utils";
import { PatientDynamicFieldType } from "@/types/enums/patient-health-field";
import { PatientDetail } from "@/types/patient";
import UnscreenedMarker from "./unscreened-marker";
interface PatientHealthSectionProps {
  patient: PatientDetail;
  shouldUseTwoColumns: boolean;
}
export default function PatientHealthSection(t0) {
  const $ = _c(54);
  const {
    patient,
    shouldUseTwoColumns
  } = t0;
  let t1;
  if ($[0] !== patient || $[1] !== shouldUseTwoColumns) {
    const selfReportedMedications = getMedications(patient);
    const unscreenedMedications = getUnscreenedValues(patient, PatientDynamicFieldType.MEDICATIONS);
    const unscreenedAllergies = getUnscreenedValues(patient, PatientDynamicFieldType.ALLERGIES);
    const unscreenedPrescriptions = getUnscreenedValues(patient, PatientDynamicFieldType.PRESCRIPTIONS);
    const recognizedTypes = Object.values(PatientDynamicFieldType);
    const additionalFields = patient.healthData ? Object.entries(patient.healthData).filter(t2 => {
      const [, fieldData] = t2;
      const type = fieldData?.type;
      return !type || !recognizedTypes.includes(type as PatientDynamicFieldType);
    }) : [];
    const t3 = shouldUseTwoColumns ? "grid-cols-2" : "grid-cols-1";
    let t4;
    if ($[3] !== t3) {
      t4 = cx("grid gap-6 pr-4", t3);
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <h4 className="text-body-emphasis text-foreground">Medications</h4>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Button variant="secondary" size="sm" icon={FiEdit} className="ml-4">Manage Medications</Button>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== patient.id) {
      t7 = <div className="mb-4 flex items-center justify-between">{t5}<PatientMedicationsDialog patientId={patient.id}>{t6}</PatientMedicationsDialog></div>;
      $[7] = patient.id;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    const t8 = selfReportedMedications && selfReportedMedications.length > 0 ? <ul className="space-y-2">{selfReportedMedications.map((medication, idx) => <li key={idx} className="text-body text-foreground">{medication}{unscreenedMedications.has(medication) && <UnscreenedMarker kind="medication" />}</li>)}</ul> : <p className="text-body text-muted-foreground">None recorded</p>;
    let t9;
    if ($[9] !== t7 || $[10] !== t8) {
      t9 = <div>{t7}{t8}</div>;
      $[9] = t7;
      $[10] = t8;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <h4 className="text-body-emphasis text-foreground">Allergies</h4>;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Button variant="secondary" size="sm" icon={FiEdit} className="ml-4">Manage Allergies</Button>;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== patient.id) {
      t12 = <div className="mb-4 flex items-center justify-between">{t10}<AllergiesDialog patientId={patient.id}>{t11}</AllergiesDialog></div>;
      $[14] = patient.id;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    const allergies = getAllergies(patient);
    const t13 = allergies.length > 0 ? <ul className="space-y-2">{allergies.map((allergy, idx_0) => <li key={idx_0} className="text-body text-foreground">{allergy}{unscreenedAllergies.has(allergy) && <UnscreenedMarker kind="allergy" />}</li>)}</ul> : <p className="text-body text-muted-foreground">None recorded</p>;
    let t14;
    if ($[16] !== t12 || $[17] !== t13) {
      t14 = <div>{t12}{t13}</div>;
      $[16] = t12;
      $[17] = t13;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    let t15;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <h4 className="text-body-emphasis text-foreground">Family History</h4>;
      $[19] = t15;
    } else {
      t15 = $[19];
    }
    let t16;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <Button variant="secondary" size="sm" icon={FiEdit} className="ml-4">Manage Family History</Button>;
      $[20] = t16;
    } else {
      t16 = $[20];
    }
    let t17;
    if ($[21] !== patient.id) {
      t17 = <div className="mb-4 flex items-center justify-between">{t15}<FamilyHistoryDialog patientId={patient.id}>{t16}</FamilyHistoryDialog></div>;
      $[21] = patient.id;
      $[22] = t17;
    } else {
      t17 = $[22];
    }
    let t18;
    if ($[23] !== patient) {
      const familyHistory = getFamilyHistory(patient);
      t18 = familyHistory.length > 0 ? <ul className="space-y-2">{familyHistory.map(_temp)}</ul> : <p className="text-body text-muted-foreground">None recorded</p>;
      $[23] = patient;
      $[24] = t18;
    } else {
      t18 = $[24];
    }
    const t19 = t18;
    let t20;
    if ($[25] !== t17 || $[26] !== t19) {
      t20 = <div>{t17}{t19}</div>;
      $[25] = t17;
      $[26] = t19;
      $[27] = t20;
    } else {
      t20 = $[27];
    }
    let t21;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <h4 className="text-body-emphasis text-foreground">Medical History</h4>;
      $[28] = t21;
    } else {
      t21 = $[28];
    }
    let t22;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <Button variant="secondary" size="sm" icon={FiEdit} className="ml-4">Manage Medical History</Button>;
      $[29] = t22;
    } else {
      t22 = $[29];
    }
    let t23;
    if ($[30] !== patient.id) {
      t23 = <div className="mb-4 flex items-center justify-between">{t21}<MedicalHistoryDialog patientId={patient.id}>{t22}</MedicalHistoryDialog></div>;
      $[30] = patient.id;
      $[31] = t23;
    } else {
      t23 = $[31];
    }
    let t24;
    if ($[32] !== patient) {
      const medicalHistory = getMedicalHistory(patient);
      t24 = medicalHistory.length > 0 ? <ul className="space-y-2">{medicalHistory.map(_temp2)}</ul> : <p className="text-body text-muted-foreground">None recorded</p>;
      $[32] = patient;
      $[33] = t24;
    } else {
      t24 = $[33];
    }
    const t25 = t24;
    let t26;
    if ($[34] !== t23 || $[35] !== t25) {
      t26 = <div>{t23}{t25}</div>;
      $[34] = t23;
      $[35] = t25;
      $[36] = t26;
    } else {
      t26 = $[36];
    }
    let t27;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <h4 className="text-body-emphasis text-foreground">Prescriptions</h4>;
      $[37] = t27;
    } else {
      t27 = $[37];
    }
    let t28;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <Button variant="secondary" size="sm" icon={FiEdit} className="ml-4">Manage Prescriptions</Button>;
      $[38] = t28;
    } else {
      t28 = $[38];
    }
    let t29;
    if ($[39] !== patient.id) {
      t29 = <div className="mb-4 flex items-center justify-between">{t27}<PrescriptionsDialog patientId={patient.id}>{t28}</PrescriptionsDialog></div>;
      $[39] = patient.id;
      $[40] = t29;
    } else {
      t29 = $[40];
    }
    const prescriptions = getPrescriptions(patient);
    const t30 = prescriptions.length > 0 ? <ul className="space-y-2">{prescriptions.map((p, idx_3) => <li key={idx_3} className="text-body text-foreground">{p}{unscreenedPrescriptions.has(p) && <UnscreenedMarker kind="medication" />}</li>)}</ul> : <p className="text-body text-muted-foreground">None recorded</p>;
    let t31;
    if ($[41] !== t29 || $[42] !== t30) {
      t31 = <div>{t29}{t30}</div>;
      $[41] = t29;
      $[42] = t30;
      $[43] = t31;
    } else {
      t31 = $[43];
    }
    let t32;
    if ($[44] !== patient) {
      t32 = getHealthFieldId(patient, PatientDynamicFieldType.PATIENT_REPORTED_SYMPTOMS) !== undefined && <div><div className="mb-4 flex items-center justify-between"><h4 className="text-body-emphasis text-foreground">Patient Reported Symptoms</h4><PatientReportedSymptomsDialog patientId={patient.id}><Button variant="secondary" size="sm" icon={FiEdit} className="ml-4">Manage Symptoms</Button></PatientReportedSymptomsDialog></div>{(() => {
          const symptoms = getPatientReportedSymptoms(patient);
          return symptoms.length > 0 ? <ul className="space-y-2">{symptoms.map(_temp3)}</ul> : <p className="text-body text-muted-foreground">None recorded</p>;
        })()}</div>;
      $[44] = patient;
      $[45] = t32;
    } else {
      t32 = $[45];
    }
    let t33;
    if ($[46] !== t14 || $[47] !== t20 || $[48] !== t26 || $[49] !== t31 || $[50] !== t32 || $[51] !== t4 || $[52] !== t9) {
      t33 = <CollapsibleSection title="Patient Reported Information" persistKey="patient-health"><div className={t4}>{t9}{t14}{t20}{t26}{t31}{t32}</div></CollapsibleSection>;
      $[46] = t14;
      $[47] = t20;
      $[48] = t26;
      $[49] = t31;
      $[50] = t32;
      $[51] = t4;
      $[52] = t9;
      $[53] = t33;
    } else {
      t33 = $[53];
    }
    t1 = <div className="space-y-12">{t33}{additionalFields.length > 0 && <CollapsibleSection title="Additional Health Information" persistKey="additional-health-information" actions={<DynamicFieldsDialog><Button variant="secondary" size="sm" icon={FiEdit}>Edit</Button></DynamicFieldsDialog>}><div className={cx("grid gap-x-6", shouldUseTwoColumns ? "grid-cols-2" : "grid-cols-1")}>{additionalFields.map(_temp6)}</div></CollapsibleSection>}</div>;
    $[0] = patient;
    $[1] = shouldUseTwoColumns;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp6(t0) {
  const [key, fieldData_0] = t0;
  const values = (fieldData_0?.values ?? []).map(String).filter(_temp4);
  return <LabeledValue key={key} label={formatToHumanReadable(key)} value={values.length === 0 ? "None recorded" : <ul className="max-h-48 space-y-1 overflow-y-auto pr-2 scrollbar-visible">{values.map(_temp5)}</ul>} />;
}
function _temp5(value_0, idx_5) {
  return <li key={idx_5} className="whitespace-pre-line">{value_0}</li>;
}
function _temp4(value) {
  return value.trim();
}
function _temp3(s, idx_4) {
  return <li key={idx_4} className="text-body text-foreground">{s}</li>;
}
function _temp2(h_0, idx_2) {
  return <li key={idx_2} className="text-body text-foreground">{h_0}</li>;
}
function _temp(h, idx_1) {
  return <li key={idx_1} className="text-body text-foreground">{h}</li>;
}